import { ref, shallowRef } from 'vue'
import { isInpostPayConfig, type InpostPayConfig, type InpostPayOrderCreatedEvent } from '~~/shared/types/inpostpay'

// Kontrakt widget'u InPost Pay v2:
//   window.InPostPayWidget.init({ merchantClientId, basketBindingApiKey, language, ...callbacks })
//
// Stare globale window.inpostizi_* są ignorowane przez `inpostpay.widget.v2.js` —
// odczytywał je tylko PrestaShopowy helper (v2.<hash>.js), którego w Nuxcie nie ładujemy.
// Nasze callbacki spełniają tę samą rolę co ten helper.

type InpostPayError =
  | { kind: 'invalid-config' }
  | { kind: 'fetch-failed'; status?: number }
  | { kind: 'timeout' }
  | { kind: 'init-failed'; message: string }

const SCRIPT_READY_POLL_MS = 200
const SCRIPT_READY_TIMEOUT_MS = 10_000
const ORDER_EVENT = 'inpostpay:order-created'
const BASKET_EVENT = 'inpostpay:basket-changed'
// Po złożeniu zamówienia w aplikacji InPost widget woła handleBasketEvent('orderCreated').
// orderId pobieramy z merchant-route confirmation-url (cookie inpostizi_basket_id → sesja
// koszyka → redirect_url z id_order). Event dociera przez polling widget-api InPost, czyli
// ścieżką NIEZALEŻNĄ od naszego POST /order, który finalizuje sesję — może więc wyprzedzić
// finalizację. Dlatego krótko ponawiamy, zanim uznamy, że orderId nie będzie dostępny.
const ORDER_CONFIRM_MAX_ATTEMPTS = 4
const ORDER_CONFIRM_RETRY_MS = 800

interface InpostPayInitOptions {
  merchantClientId: string
  basketBindingApiKey?: string
  language?: string
  webView?: boolean
  unboundWidgetClicked?: (productId?: string) => Promise<string | undefined>
  handleBasketEvent?: (event: string) => boolean | Promise<boolean>
  apiBaseUrl?: string
}

interface InpostPayWidgetInstance {
  rerender?: () => void
  refresh?: () => void
}

declare global {
  interface Window {
    InPostPayWidget?: {
      init: (opts: InpostPayInitOptions) => InpostPayWidgetInstance
    }
    __INPOST_PAY_WIDGET__?: unknown
  }
}

const _isReady = ref(false)
const _error = shallowRef<InpostPayError | null>(null)
const _config = shallowRef<InpostPayConfig | null>(null)
let _initPromise: Promise<void> | null = null
let _scriptEl: HTMLScriptElement | null = null
let _widgetInstance: InpostPayWidgetInstance | null = null
// Handler dodania produktu do koszyka dla karty produktu (binding_place=PRODUCT_CARD).
// Composable nie może sam utworzyć mutacji TanStack (wymaga kontekstu setup), więc
// komponent InpostPayButton rejestruje go na mount, a unboundWidgetClicked go woła.
let _productAddHandler: (() => Promise<void>) | null = null

function injectScript(src: string): HTMLScriptElement {
  const existing = document.head.querySelector<HTMLScriptElement>('script[data-inpostpay]')
  if (existing) return existing
  const s = document.createElement('script')
  s.src = src
  s.async = true
  s.dataset.inpostpay = '1'
  document.head.appendChild(s)
  return s
}

function waitForWidgetApi(): Promise<boolean> {
  return new Promise((resolve) => {
    let elapsed = 0
    const tick = () => {
      if (typeof window.InPostPayWidget?.init === 'function') return resolve(true)
      elapsed += SCRIPT_READY_POLL_MS
      if (elapsed >= SCRIPT_READY_TIMEOUT_MS) return resolve(false)
      setTimeout(tick, SCRIPT_READY_POLL_MS)
    }
    setTimeout(tick, SCRIPT_READY_POLL_MS)
  })
}

async function fetchFreshBindingKey(): Promise<string | undefined> {
  try {
    const raw = await $fetch('/api/store/inpostpay/binding-key') as Record<string, unknown>
    if (raw && typeof raw === 'object' && typeof raw.bindingApiKey === 'string' && raw.bindingApiKey.length > 0) {
      return raw.bindingApiKey
    }
    return undefined
  }
  catch {
    return undefined
  }
}

async function fetchOrderConfirmation(): Promise<{ url: string, orderId: string | null } | null> {
  try {
    const raw = await $fetch('/api/store/inpostpay/confirmation-url') as { url?: unknown, orderId?: unknown }
    if (raw && typeof raw === 'object' && typeof raw.url === 'string' && raw.url.length > 0) {
      return { url: raw.url, orderId: typeof raw.orderId === 'string' ? raw.orderId : null }
    }
    return null
  }
  catch {
    // Sesja koszyka mogła jeszcze nie zostać sfinalizowana (server route zwraca wtedy
    // błąd). Cisza — handleOrderCreated ponowi próbę.
    return null
  }
}

async function handleOrderCreated(): Promise<void> {
  if (typeof window === 'undefined') return

  let orderId: string | null = null
  for (let attempt = 0; attempt < ORDER_CONFIRM_MAX_ATTEMPTS; attempt += 1) {
    const res = await fetchOrderConfirmation()
    if (res?.orderId) {
      orderId = res.orderId
      break
    }
    if (attempt < ORDER_CONFIRM_MAX_ATTEMPTS - 1) {
      await new Promise<void>((resolve) => { setTimeout(resolve, ORDER_CONFIRM_RETRY_MS) })
    }
  }

  // Dispatch ZAWSZE — useInpostPayOrderConfirmation nawiguje na /thank-you. Z order_id,
  // jeśli udało się go pobrać; bez — jeśli mimo ponowień nie był gotowy (strona thank-you
  // obsługuje brak numeru). Priorytetem jest przekierowanie po zamówieniu z aplikacji.
  window.dispatchEvent(new CustomEvent(ORDER_EVENT, { detail: { orderId: orderId ?? '' } }))
}

function buildInitOptions(config: InpostPayConfig): InpostPayInitOptions {
  return {
    merchantClientId: config.merchantClientId,
    basketBindingApiKey: config.bindingApiKey ?? undefined,
    language: 'pl',
    webView: false,
    unboundWidgetClicked: async () => {
      // Kontrakt Widget 2.0: na karcie produktu (binding_place=PRODUCT_CARD) widget woła
      // ten callback, aby merchant DODAŁ produkt do koszyka i zwrócił świeży binding key.
      // Stąd addToCart wykonujemy tutaj (przez handler zarejestrowany przez komponent),
      // a nie przez DOM-intercept + syntetyczny klik (który widget ignorował — isTrusted:false).
      if (_productAddHandler) {
        try {
          await _productAddHandler()
        }
        catch {
          return undefined
        }
      }
      return await fetchFreshBindingKey()
    },
    handleBasketEvent: (event) => {
      if (typeof window === 'undefined') return true

      // orderCreated: klient złożył zamówienie w aplikacji InPost. Pobieramy orderId
      // i przekierowujemy przeglądarkę na /thank-you (headless odpowiednik tego, co
      // PrestaShopowy helper robi przez window.location.href = confirmation-url).
      if (event === 'orderCreated') {
        void handleOrderCreated()
        return true
      }

      // Live-sync koszyka: gdy klient zmieni ilość / usunie produkt w aplikacji InPost,
      // widget odpala event basketowy. Backend (inpostizi BasketEventHandler →
      // ProductsQuantityEventHandler::$cart->updateQty/deleteProduct) JUŻ zaktualizował
      // PrestaShopowy \Cart, więc wystarczy odświeżyć query koszyka we froncie — bez
      // pełnego reloadu strony. Composable jest singletonem bez kontekstu setup, więc
      // emitujemy window-event; subskrybują go zamontowane komponenty (useQueryClient).
      window.dispatchEvent(new CustomEvent(BASKET_EVENT, { detail: { event } }))
      return true
    },
  }
}

async function callWidgetInit(config: InpostPayConfig): Promise<boolean> {
  if (typeof window.InPostPayWidget?.init !== 'function') {
    return false
  }

  try {
    _widgetInstance = window.InPostPayWidget.init(buildInitOptions(config))
    return true
  }
  catch (e) {
    const msg = e instanceof Error ? e.message : String(e)
    _error.value = { kind: 'init-failed', message: msg }
    return false
  }
}

async function doInit(): Promise<void> {
  let raw: unknown
  try {
    raw = await $fetch('/api/store/inpostpay/widget-config')
  }
  catch (err) {
    const e = err as { statusCode?: number; statusMessage?: string }
    _error.value = { kind: 'fetch-failed', status: e?.statusCode }
    return
  }

  if (!isInpostPayConfig(raw)) {
    _error.value = { kind: 'invalid-config' }
    return
  }

  _config.value = raw
  _scriptEl = injectScript(raw.widgetScriptUrl)

  const apiReady = await waitForWidgetApi()
  if (!apiReady) {
    _error.value = { kind: 'timeout' }
    return
  }

  const initOk = await callWidgetInit(raw)
  if (!initOk) return

  _isReady.value = true
  _error.value = null
}

async function refreshBindingKey(): Promise<void> {
  // Bazowa konfiguracja (merchant_client_id, środowisko, URL skryptu) pochodzi z init()
  // przez `widget-config`. TUTAJ generujemy/odświeżamy binding key przez merchant-route
  // `getBindingKey` (`/api/store/inpostpay/binding-key`), który server-side wywołuje
  // `initializeBasketBinding` i rejestruje koszyk w InPost BasketApp, a następnie
  // re-inicjalizujemy widget ze świeżym kluczem. To headless odpowiednik standardowego
  // PrestaShopowego `inpostizi_fetch_binding_key`.
  //
  // Wcześniej ta funkcja czytała tylko `widget-config` (które NIGDY nie generuje klucza),
  // więc widget nigdy nie był związany z koszykiem (bug F2).
  const base = _config.value
  if (!base) {
    return
  }

  const key = await fetchFreshBindingKey()
  const previousBinding = base.bindingApiKey ?? null
  const nextBinding = key ?? null

  const nextConfig: InpostPayConfig = { ...base, bindingApiKey: nextBinding, needsCart: nextBinding === null }
  _config.value = nextConfig

  if (!_widgetInstance) {
    // init() jeszcze nie zainicjalizował widgetu (np. timeout API skryptu) — spróbuj teraz.
    if (typeof window.InPostPayWidget?.init === 'function') {
      const ok = await callWidgetInit(nextConfig)
      if (ok) {
        _isReady.value = true
        _error.value = null
      }
    }
    return
  }

  if (previousBinding === nextBinding) {
    _widgetInstance.rerender?.()
    return
  }

  // Singleton w widget.v2.js (window.__INPOST_PAY_WIDGET__) blokuje ponowny init
  // z nowymi opcjami. Żeby widget zauważył świeży binding key, czyścimy singleton
  // i wywołujemy init() ponownie.
  delete window.__INPOST_PAY_WIDGET__
  _widgetInstance = null
  await callWidgetInit(nextConfig)
}

function setProductAddHandler(fn: () => Promise<void>): void {
  _productAddHandler = fn
}

function clearProductAddHandler(): void {
  _productAddHandler = null
}

function rerenderWidget(): void {
  // Widget skanuje <inpost-izi-button> WYŁĄCZNIE podczas renderu (ve() → żywe
  // getElementsByTagName, atrybuty czytane getAttribute na bieżąco) i NIE ma ani
  // observera na wstawianie elementów, ani attributeChangedCallback. Po przejściu na
  // inny produkt świeżo utworzony / zmieniony <inpost-izi-button> pozostaje pusty,
  // dopóki nie wymusimy re-renderu. rerender() dispatchuje RERENDER (renderForced:true),
  // co re-skanuje DOM i hydratuje aktualny przycisk. (Patrz inpostpay.widget.v2.js: Je/Ze/ve.)
  _widgetInstance?.rerender?.()
}

function doCleanup(): void {
  if (_scriptEl && _scriptEl.parentNode) {
    _scriptEl.parentNode.removeChild(_scriptEl)
  }
  _scriptEl = null
  _widgetInstance = null
  _productAddHandler = null
  delete window.__INPOST_PAY_WIDGET__
  _isReady.value = false
  _initPromise = null
  _config.value = null
}

function listenForOrderCreated(cb: (e: InpostPayOrderCreatedEvent) => void): () => void {
  const handler = (raw: Event) => {
    const detail = (raw as CustomEvent).detail as Partial<InpostPayOrderCreatedEvent> | undefined
    // Forward ZAWSZE — ORDER_EVENT emituje wyłącznie handleOrderCreated (zdarzenie celowe).
    // Gdy orderId nie udało się pobrać (race finalizacji mimo ponowień), przekazujemy pusty
    // string: useInpostPayOrderConfirmation i tak przekieruje na /thank-you (bez numeru).
    cb({ orderId: detail?.orderId ?? '', orderReference: detail?.orderReference })
  }
  window.addEventListener(ORDER_EVENT, handler)
  return () => window.removeEventListener(ORDER_EVENT, handler)
}

function listenForBasketChanged(cb: (event: string) => void): () => void {
  const handler = (raw: Event) => {
    const detail = (raw as CustomEvent).detail as { event?: string } | undefined
    cb(detail?.event ?? '')
  }
  window.addEventListener(BASKET_EVENT, handler)
  return () => window.removeEventListener(BASKET_EVENT, handler)
}

export function useInpostPay() {
  function init(): Promise<void> {
    if (_initPromise) return _initPromise
    _initPromise = doInit().then((res) => {
      if (_error.value) {
        // Allow retry on next init() call
        _initPromise = null
      }
      return res
    })
    return _initPromise
  }

  return {
    isReady: _isReady,
    error: _error,
    config: _config,
    init,
    prepareForProduct: async (_productId: string) => refreshBindingKey(),
    prepareForCart: async () => refreshBindingKey(),
    setProductAddHandler,
    clearProductAddHandler,
    rerenderWidget,
    cleanup: doCleanup,
    listenForOrderCreated,
    listenForBasketChanged,
  }
}
